// frontend/src/pages/Feedback.js
import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { getFeedback, submitFeedback, getFeedbackSummary, getFeedbackUsers, getUsers, getCycles } from '../services/api';
import {
  Card, Avatar, Badge, Btn, Modal, Field, Select, Textarea, Stars, Spinner, SectionTitle
} from '../components/UI';
import PageHeader from '../components/PageHeader';

const TYPE_COLOR = { peer: 'blue', manager: 'green', upward: 'amber', self: 'gray' };

function ScoreBubble({ label, value }) {
  if (!value) return null;
  return (
    <div style={{ textAlign: 'center', padding: '10px', background: 'var(--gray-100)', borderRadius: 8, minWidth: 80 }}>
      <div style={{ fontSize: 18, fontWeight: 500, color: 'var(--navy-600)' }}>{value}</div>
      <div style={{ fontSize: 10, color: 'var(--text-3)', marginTop: 2 }}>{label}</div>
    </div>
  );
}

export default function Feedback() {
  const { user } = useAuth();
  const [feedback,  setFeedback]  = useState([]);
  const [users,     setUsers]     = useState([]);   // access-controlled (for filter)
  const [allUsers,  setAllUsers]  = useState([]);   // all org users (for give-feedback dropdown)
  const [cycles,    setCycles]    = useState([]);
  const [summary,   setSummary]   = useState({});
  const [loading,   setLoading]   = useState(true);
  const [filterTo,  setFilterTo]  = useState('');
  const [showForm,  setShowForm]  = useState(false);
  const [saving,    setSaving]    = useState(false);

  const [form, setForm] = useState({
    to_user_id: '', cycle_id: '', feedback_type: 'peer',
    quote: '', technical_score: 3, collab_score: 3,
    visibility_score: 3, overall_stars: 4, is_anonymous: 0,
  });

  const load = async () => {
    const fb = await getFeedback().catch(() => []);
    setFeedback(Array.isArray(fb) ? fb : []);
  };

  useEffect(() => {
    Promise.all([
      getFeedback().catch(() => []),
      getUsers().catch(() => []),
      getCycles().catch(() => []),
      getFeedbackUsers().catch(() => []),
    ])
      .then(([fb, u, c, au]) => {
        setFeedback(Array.isArray(fb) ? fb : []);
        setUsers(Array.isArray(u) ? u : []);
        setCycles(Array.isArray(c) ? c : []);
        setAllUsers(Array.isArray(au) ? au : []);
      })
      .finally(() => setLoading(false));
  }, []);

  // Load summary when filter changes
  useEffect(() => {
    if (filterTo) {
      getFeedbackSummary(filterTo).then(s => setSummary(s)).catch(() => setSummary({}));
    } else {
      setSummary({});
    }
  }, [filterTo]);

  const handleSubmit = async e => {
    e.preventDefault(); setSaving(true);
    try {
      await submitFeedback({ ...form, cycle_id: form.cycle_id || null });
      await load(); setShowForm(false);
      setForm({ to_user_id: '', cycle_id: '', feedback_type: 'peer', quote: '', technical_score: 3, collab_score: 3, visibility_score: 3, overall_stars: 4, is_anonymous: 0 });
    } catch (err) { alert(err.response?.data?.error || 'Error submitting feedback'); }
    finally { setSaving(false); }
  };

  const filtered = filterTo ? feedback.filter(f => String(f.to_user_id) === filterTo) : feedback;

  if (loading) return <Spinner />;

  return (
    <div style={{ padding: '20px clamp(16px, 3vw, 32px)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <PageHeader moduleKey="feedback" helpTask="give-feedback" subtitle={'Peer, manager & upward reviews'} />
        <Btn size="sm" onClick={() => setShowForm(true)}>+ Give feedback</Btn>
      </div>

      {/* Person filter — shows if user has direct reports or is admin */}
      {users.length > 1 && (
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 20 }}>
          <span style={{ fontSize: 12, color: 'var(--text-3)', fontWeight: 500 }}>View feedback for:</span>
          <select value={filterTo} onChange={e => setFilterTo(e.target.value)}
            style={{ padding: '7px 12px', fontSize: 13, border: '0.5px solid var(--border-md)', borderRadius: 'var(--radius-md)', background: 'var(--surface)', color: 'var(--text-1)', minWidth: 200 }}>
            <option value="">Everyone</option>
            {users.map(u => <option key={u.id} value={String(u.id)}>{u.name}</option>)}
          </select>
        </div>
      )}

      {/* Summary scores */}
      {filterTo && summary?.total_responses > 0 && (
        <Card style={{ marginBottom: 20 }}>
          <SectionTitle>Aggregated scores · {(users.find(u => String(u.id) === filterTo) || allUsers.find(u => String(u.id) === filterTo))?.name}</SectionTitle>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 8 }}>
            <ScoreBubble label="Technical" value={summary.avg_technical} />
            <ScoreBubble label="Collaboration" value={summary.avg_collab} />
            <ScoreBubble label="Visibility" value={summary.avg_visibility} />
            <div style={{ textAlign: 'center', padding: '10px', background: 'var(--amber-50)', borderRadius: 8, minWidth: 80 }}>
              <div style={{ fontSize: 18, fontWeight: 500, color: 'var(--amber-700)' }}>{summary.avg_stars}</div>
              <div style={{ fontSize: 10, color: 'var(--amber-700)', marginTop: 2 }}>Overall ★</div>
            </div>
            <div style={{ textAlign: 'center', padding: '10px', background: 'var(--gray-100)', borderRadius: 8, minWidth: 80 }}>
              <div style={{ fontSize: 18, fontWeight: 500 }}>{summary.total_responses}</div>
              <div style={{ fontSize: 10, color: 'var(--text-3)', marginTop: 2 }}>Responses</div>
            </div>
          </div>
        </Card>
      )}

      {/* Feedback cards */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {filtered.map(f => (
          <Card key={f.id}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
              <Avatar initials={f.from_initials} size={32} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 500 }}>
                  {f.from_name}
                  <span style={{ fontSize: 12, fontWeight: 400, color: 'var(--text-3)' }}> → {f.to_name}</span>
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-3)' }}>
                  {new Date(f.submitted_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}
                </div>
              </div>
              <Badge color={TYPE_COLOR[f.feedback_type]}>{f.feedback_type}</Badge>
              <Stars value={f.overall_stars} />
            </div>

            {f.quote && (
              <div style={{
                fontSize: 13, color: 'var(--text-2)', lineHeight: 1.6,
                borderLeft: '2px solid var(--navy-100)', paddingLeft: 12, marginBottom: 12,
                fontStyle: 'italic',
              }}>
                "{f.quote}"
              </div>
            )}

            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {f.technical_score  != null && <div style={{ fontSize: 11, background: 'var(--gray-100)', padding: '4px 10px', borderRadius: 6, color: 'var(--text-2)' }}>Technical: <strong>{f.technical_score}</strong></div>}
              {f.collab_score     != null && <div style={{ fontSize: 11, background: 'var(--gray-100)', padding: '4px 10px', borderRadius: 6, color: 'var(--text-2)' }}>Collaboration: <strong>{f.collab_score}</strong></div>}
              {f.visibility_score != null && <div style={{ fontSize: 11, background: 'var(--gray-100)', padding: '4px 10px', borderRadius: 6, color: 'var(--text-2)' }}>Visibility: <strong>{f.visibility_score}</strong></div>}
            </div>
          </Card>
        ))}
        {filtered.length === 0 && (
          <div style={{ textAlign: 'center', padding: '48px 0', color: 'var(--text-3)', fontSize: 13 }}>
            No feedback yet. Be the first to give some!
          </div>
        )}
      </div>

      {/* Give feedback modal */}
      <Modal open={showForm} onClose={() => setShowForm(false)} title="Give feedback">
        <form onSubmit={handleSubmit}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field label="For" required>
              <Select value={form.to_user_id} onChange={e => setForm(f => ({ ...f, to_user_id: e.target.value }))}>
                <option value="">Select person…</option>
                {allUsers.filter(u => u.id !== user?.id).map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
              </Select>
            </Field>
            <Field label="Type">
              <Select value={form.feedback_type} onChange={e => setForm(f => ({ ...f, feedback_type: e.target.value }))}>
                <option value="peer">Peer</option>
                <option value="manager">Manager</option>
                <option value="upward">Upward</option>
                <option value="self">Self</option>
              </Select>
            </Field>
          </div>
          <Field label="Feedback">
            <Textarea value={form.quote} onChange={e => setForm(f => ({ ...f, quote: e.target.value }))} placeholder="Share specific observations and examples…" rows={4} />
          </Field>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
            {[['Technical skill', 'technical_score'], ['Collaboration', 'collab_score'], ['Visibility', 'visibility_score']].map(([label, key]) => (
              <Field key={key} label={`${label}: ${form[key]}`}>
                <input type="range" min="1" max="5" step="0.5" value={form[key]}
                  onChange={e => setForm(f => ({ ...f, [key]: parseFloat(e.target.value) }))}
                  style={{ width: '100%', accentColor: 'var(--navy-500)' }} />
              </Field>
            ))}
          </div>
          <Field label={`Overall: ${'★'.repeat(form.overall_stars)}${'☆'.repeat(5 - form.overall_stars)}`}>
            <input type="range" min="1" max="5" step="1" value={form.overall_stars}
              onChange={e => setForm(f => ({ ...f, overall_stars: parseInt(e.target.value) }))}
              style={{ width: '100%', accentColor: 'var(--amber-500)' }} />
          </Field>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
            <input type="checkbox" id="anon" checked={form.is_anonymous === 1}
              onChange={e => setForm(f => ({ ...f, is_anonymous: e.target.checked ? 1 : 0 }))} />
            <label htmlFor="anon" style={{ fontSize: 13, color: 'var(--text-2)', cursor: 'pointer' }}>Submit anonymously</label>
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Btn type="button" variant="secondary" onClick={() => setShowForm(false)}>Cancel</Btn>
            <Btn disabled={saving || !form.to_user_id}>{saving ? 'Submitting…' : 'Submit feedback'}</Btn>
          </div>
        </form>
      </Modal>
    </div>
  );
}
